import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { useSyncWishlist as useCoreSyncWishlist } from '@shopware/composables'
import { useShopwareContext } from '#imports'
import type { Schemas } from '#shopware'

export type UseSyncWishlistReturn = {
    getWishlistProducts(defaultSearchCriteria?: Schemas['Criteria']): Promise<void>
    mergeWishlistProducts(itemsToMerge: string[], configs?: Record<string, string>): Promise<void>
    addToWishlistSync(id: string, config?: string): Promise<void>
    removeFromWishlistSync(id: string): Promise<void>
    items: ComputedRef<string[]>
    count: ComputedRef<number>
    currentPage: ComputedRef<number>
    limit: ComputedRef<number>
    products: ComputedRef<Schemas['Product'][]>
    isLoading: Ref<boolean>
    excentosConfigurations: ComputedRef<Record<string, string>>
}

const _wishlistItems: Ref<string[]> = ref([])
const _wishlistProducts: Ref<Schemas['Product'][]> = ref([])
const _excentosConfigurations: Ref<Record<string, string>> = ref({})
const _currentPage: Ref<number> = ref(1)
const _limit: Ref<number> = ref(15)
const _totalWishlistItemsCount: Ref<number> = ref(0)

export function useSyncWishlist(): UseSyncWishlistReturn {
    const core = useCoreSyncWishlist()
    const { apiClient } = useShopwareContext()

    async function addToWishlistSync(id: string, config?: string) {
        if (config == null) {
            return core.addToWishlistSync(id)
        }

        await apiClient.invoke(
            'addProductOnWishlist post /customer/wishlist/add/{productId}',
            {
                pathParams: { productId: id },
                body: { payload: { custom_cart_excentos_configuration: config } },
            },
        )
    }

    async function mergeWishlistProducts(itemsToMerge: string[], configs?: Record<string, string>) {
        if (configs == null || Object.keys(configs).length === 0) {
            return core.mergeWishlistProducts(itemsToMerge)
        }

        await apiClient.invoke(
            'mergeProductOnWishlist post /customer/wishlist/merge',
            {
                body: {
                    productIds: itemsToMerge,
                    payload: { custom_cart_excentos_configuration: configs },
                },
            },
        )
    }

    async function getWishlistProducts(defaultSearchCriteria?: Schemas['Criteria']) {
        try {
            const response = await apiClient.invoke(
                'readCustomerWishlist post /customer/wishlist',
                { body: { ...defaultSearchCriteria, 'total-count-mode': 'exact' } },
            )
            const elements = response.data.products.elements ?? []
            _wishlistItems.value = elements.map((element) => element.id)
            _wishlistProducts.value = elements as unknown as Schemas['Product'][]
            _excentosConfigurations.value = response.data.wishlist?.customFields?.custom_cart_excentos_configuration ?? {}
            _totalWishlistItemsCount.value = response.data.products.total ?? 0
            _currentPage.value = response.data.products.page ?? 1
            _limit.value = response.data.products.limit ?? 15
        } catch (error) {
            console.error('[useSyncWishlist][getWishlistProducts][error]:', error)
            _wishlistItems.value = []
            _wishlistProducts.value = []
            _excentosConfigurations.value = {}
            _totalWishlistItemsCount.value = 0
        }
    }

    return {
        getWishlistProducts,
        addToWishlistSync,
        // Upstream's own type signature under-declares this as `void`, even though the
        // implementation is async — matches the same gap `addToWishlistSync` closes above.
        removeFromWishlistSync: core.removeFromWishlistSync as (id: string) => Promise<void>,
        mergeWishlistProducts,
        products: computed(() => _wishlistProducts.value),
        items: computed(() => _wishlistItems.value),
        count: computed(() => _totalWishlistItemsCount.value),
        currentPage: computed(() => _currentPage.value),
        isLoading: core.isLoading,
        limit: computed(() => _limit.value),
        excentosConfigurations: computed(() => _excentosConfigurations.value),
    }
}
